/**
 * Compact filter + sort bar for the Per-Article Signals section.
 *
 * Four filter chips (Direction / Magnitude / Horizon / Category) — each
 * opens a popover with multi-select checkboxes. One sort dropdown. A reset
 * button appears when anything is active. Result count is rendered alongside.
 *
 * All state is controlled — the parent owns `filters` and `sort` so the
 * filtering/sorting math can live in the tab.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import type { SourcingArticleAnalysis } from '../sourcing-intelligence';

// ── Types ────────────────────────────────────────────────────────────────────

export type DirectionFilter = 'bullish' | 'bearish' | 'neutral';
export type MagnitudeFilter = 'high' | 'medium' | 'low';
export type HorizonFilter = 'immediate' | 'short' | 'medium' | 'long';
export type CategoryFilter =
  | 'revenue'
  | 'margin'
  | 'growth'
  | 'competitive'
  | 'regulatory'
  | 'capital_allocation'
  | 'leadership'
  | 'operations'
  | 'technology'
  | 'macro';

export interface ArticleFilters {
  direction: Set<DirectionFilter>;
  magnitude: Set<MagnitudeFilter>;
  horizon: Set<HorizonFilter>;
  category: Set<CategoryFilter>;
}

export type ArticleSort =
  | 'newest'
  | 'oldest'
  | 'impact_high'
  | 'bullish_first'
  | 'bearish_first';

export const EMPTY_FILTERS: ArticleFilters = {
  direction: new Set(),
  magnitude: new Set(),
  horizon: new Set(),
  category: new Set(),
};

export const DEFAULT_SORT: ArticleSort = 'newest';

// ── Option definitions ───────────────────────────────────────────────────────

const DIRECTION_OPTIONS: Array<{ value: DirectionFilter; label: string; dot: string }> = [
  { value: 'bullish', label: 'Bullish', dot: 'bg-emerald-500' },
  { value: 'bearish', label: 'Bearish', dot: 'bg-rose-500' },
  { value: 'neutral', label: 'Neutral', dot: 'bg-neutral-400' },
];

const MAGNITUDE_OPTIONS: Array<{ value: MagnitudeFilter; label: string }> = [
  { value: 'high', label: 'High impact' },
  { value: 'medium', label: 'Medium impact' },
  { value: 'low', label: 'Low impact' },
];

const HORIZON_OPTIONS: Array<{ value: HorizonFilter; label: string }> = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'short', label: 'Short term' },
  { value: 'medium', label: 'Medium term' },
  { value: 'long', label: 'Long term' },
];

const CATEGORY_OPTIONS: Array<{ value: CategoryFilter; label: string }> = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'margin', label: 'Margin' },
  { value: 'growth', label: 'Growth' },
  { value: 'competitive', label: 'Competitive' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'capital_allocation', label: 'Capital allocation' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'operations', label: 'Operations' },
  { value: 'technology', label: 'Technology' },
  { value: 'macro', label: 'Macro' },
];

const SORT_OPTIONS: Array<{ value: ArticleSort; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'impact_high', label: 'Highest impact' },
  { value: 'bullish_first', label: 'Bullish first' },
  { value: 'bearish_first', label: 'Bearish first' },
];

// ── Filter / sort math (pure, exported for tests + the tab) ─────────────────

export function hasAnyFilter(filters: ArticleFilters): boolean {
  return (
    filters.direction.size > 0 ||
    filters.magnitude.size > 0 ||
    filters.horizon.size > 0 ||
    filters.category.size > 0
  );
}

export function applyFiltersAndSort(
  articles: SourcingArticleAnalysis[],
  filters: ArticleFilters,
  sort: ArticleSort
): SourcingArticleAnalysis[] {
  // Filter
  const filtered = articles.filter((a) => {
    if (
      filters.direction.size > 0 &&
      (!a.direction_of_impact || !filters.direction.has(a.direction_of_impact as DirectionFilter))
    ) {
      return false;
    }
    if (
      filters.magnitude.size > 0 &&
      (!a.magnitude || !filters.magnitude.has(a.magnitude as MagnitudeFilter))
    ) {
      return false;
    }
    if (
      filters.horizon.size > 0 &&
      (!a.time_horizon || !filters.horizon.has(a.time_horizon as HorizonFilter))
    ) {
      return false;
    }
    if (
      filters.category.size > 0 &&
      (!a.category_impact || !filters.category.has(a.category_impact as CategoryFilter))
    ) {
      return false;
    }
    return true;
  });

  // Sort (stable copy — never mutate input)
  const sorted = [...filtered];
  const magnitudeRank: Record<string, number> = { high: 3, medium: 2, low: 1 };
  const dateMs = (a: SourcingArticleAnalysis): number => {
    if (!a.article_published_date) return 0;
    const t = Date.parse(a.article_published_date);
    return Number.isNaN(t) ? 0 : t;
  };
  switch (sort) {
    case 'newest':
      sorted.sort((a, b) => dateMs(b) - dateMs(a));
      break;
    case 'oldest':
      sorted.sort((a, b) => dateMs(a) - dateMs(b));
      break;
    case 'impact_high':
      sorted.sort(
        (a, b) =>
          (magnitudeRank[b.magnitude ?? ''] ?? 0) -
          (magnitudeRank[a.magnitude ?? ''] ?? 0) ||
          dateMs(b) - dateMs(a)
      );
      break;
    case 'bullish_first':
      sorted.sort((a, b) => {
        const rank = (d?: string | null) => (d === 'bullish' ? 0 : d === 'neutral' ? 1 : 2);
        return rank(a.direction_of_impact) - rank(b.direction_of_impact) || dateMs(b) - dateMs(a);
      });
      break;
    case 'bearish_first':
      sorted.sort((a, b) => {
        const rank = (d?: string | null) => (d === 'bearish' ? 0 : d === 'neutral' ? 1 : 2);
        return rank(a.direction_of_impact) - rank(b.direction_of_impact) || dateMs(b) - dateMs(a);
      });
      break;
  }
  return sorted;
}

// ── Generic popover (click-outside aware) ───────────────────────────────────

interface PopoverChipProps {
  label: string;
  activeCount: number;
  children: (close: () => void) => React.ReactNode;
}

function PopoverChip({ label, activeCount, children }: PopoverChipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = activeCount > 0;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
          active
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{label}</span>
        {active && (
          <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-semibold text-white">
            {activeCount}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          className="absolute left-0 z-20 mt-1.5 w-56 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
          role="listbox"
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

interface CheckOptionProps {
  selected: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CheckOption({ selected, onToggle, children }: CheckOptionProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-neutral-700 transition-colors hover:bg-neutral-50"
      role="option"
      aria-selected={selected}
    >
      <span className="flex items-center gap-2">{children}</span>
      {selected && <Check className="h-3.5 w-3.5 text-primary-600" />}
    </button>
  );
}

// ── Main filter bar ─────────────────────────────────────────────────────────

interface PerArticleFilterBarProps {
  filters: ArticleFilters;
  onFiltersChange: (next: ArticleFilters) => void;
  sort: ArticleSort;
  onSortChange: (next: ArticleSort) => void;
  totalCount: number;
  filteredCount: number;
}

export function PerArticleFilterBar({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  totalCount,
  filteredCount,
}: PerArticleFilterBarProps) {
  const anyActive = hasAnyFilter(filters);

  const toggle = <K extends keyof ArticleFilters>(
    key: K,
    value: ArticleFilters[K] extends Set<infer V> ? V : never
  ) => {
    const next = { ...filters, [key]: new Set(filters[key] as Set<unknown>) } as ArticleFilters;
    const s = next[key] as Set<unknown>;
    if (s.has(value)) s.delete(value);
    else s.add(value);
    onFiltersChange(next);
  };

  const reset = () => onFiltersChange({ ...EMPTY_FILTERS, ...{
    direction: new Set(),
    magnitude: new Set(),
    horizon: new Set(),
    category: new Set(),
  }});

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/40 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {/* Direction */}
        <PopoverChip label="Direction" activeCount={filters.direction.size}>
          {() => (
            <div className="py-1">
              {DIRECTION_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt.value}
                  selected={filters.direction.has(opt.value)}
                  onToggle={() => toggle('direction', opt.value)}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${opt.dot}`} />
                  {opt.label}
                </CheckOption>
              ))}
            </div>
          )}
        </PopoverChip>

        {/* Magnitude */}
        <PopoverChip label="Magnitude" activeCount={filters.magnitude.size}>
          {() => (
            <div className="py-1">
              {MAGNITUDE_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt.value}
                  selected={filters.magnitude.has(opt.value)}
                  onToggle={() => toggle('magnitude', opt.value)}
                >
                  {opt.label}
                </CheckOption>
              ))}
            </div>
          )}
        </PopoverChip>

        {/* Horizon */}
        <PopoverChip label="Horizon" activeCount={filters.horizon.size}>
          {() => (
            <div className="py-1">
              {HORIZON_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt.value}
                  selected={filters.horizon.has(opt.value)}
                  onToggle={() => toggle('horizon', opt.value)}
                >
                  {opt.label}
                </CheckOption>
              ))}
            </div>
          )}
        </PopoverChip>

        {/* Category */}
        <PopoverChip label="Category" activeCount={filters.category.size}>
          {() => (
            <div className="max-h-72 overflow-y-auto py-1">
              {CATEGORY_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt.value}
                  selected={filters.category.has(opt.value)}
                  onToggle={() => toggle('category', opt.value)}
                >
                  {opt.label}
                </CheckOption>
              ))}
            </div>
          )}
        </PopoverChip>

        {/* Spacer pushes sort + reset + count to the right on wider screens */}
        <div className="flex-1" />

        {/* Sort — single-select, native style for accessibility / keyboard */}
        <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500">
          <span>Sort:</span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as ArticleSort)}
            className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {anyActive && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
          >
            <X className="h-3 w-3" />
            Reset
          </button>
        )}
      </div>

      {/* Count row */}
      <p className="mt-1.5 text-[11px] text-neutral-500">
        {filteredCount === totalCount ? (
          <>Showing all <span className="font-semibold text-neutral-700">{totalCount}</span> articles</>
        ) : (
          <>
            Showing <span className="font-semibold text-neutral-700">{filteredCount}</span> of{' '}
            <span className="font-semibold text-neutral-700">{totalCount}</span> articles
          </>
        )}
      </p>
    </div>
  );
}
